// frontend/src/pages/CsvUploadPage.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService, CsvUploadResponse, CsvProcessBatchResponse } from '../services/api';

interface UploadStats {
  totalRows: number;
  processed: number;
  remaining: number;
  newProblems: number;
  updatedProblems: number;
  failed: number;
  errors: string[];
}

export const CsvUploadPage: React.FC = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [autoProcess, setAutoProcess] = useState(false);
  const [stats, setStats] = useState<UploadStats | null>(null);
  const [completed, setCompleted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.type === 'text/csv' || selectedFile.name.endsWith('.csv')) {
        setFile(selectedFile);
        setError(null);
      } else {
        setError('Please select a CSV file');
        setFile(null);
      }
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const response: CsvUploadResponse = await apiService.uploadCsv(file);
      setSessionId(response.sessionId);
      setStats({
        totalRows: response.totalRows,
        processed: 0,
        remaining: response.totalRows,
        newProblems: 0,
        updatedProblems: 0,
        failed: 0,
        errors: [],
      });
      console.log('CSV uploaded successfully:', response);
    } catch (error: any) {
      setError(error.message || 'Failed to upload CSV');
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleProcessBatch = async () => {
    if (!sessionId) return;

    setProcessing(true);
    setError(null);

    try {
      const response: CsvProcessBatchResponse = await apiService.processCsvBatch({
        sessionId,
        batchSize: 5,
        autoProcess,
      });

      setStats(prev => prev ? {
        ...prev,
        processed: prev.totalRows - response.remaining,
        remaining: response.remaining,
        newProblems: response.newProblems,
        updatedProblems: response.updatedProblems,
        failed: response.failed,
        errors: response.errors || [],
      } : null);

      if (response.completed) {
        setCompleted(true);
        setProcessing(false);
      } else if (autoProcess) {
        // Continue processing automatically
        setTimeout(() => handleProcessBatch(), 1000);
      } else {
        setProcessing(false);
      }
    } catch (error: any) {
      setError(error.message || 'Failed to process batch');
      setProcessing(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setSessionId(null);
    setStats(null);
    setCompleted(false);
    setError(null);
    setAutoProcess(false);
    setUploading(false);
    setProcessing(false);
  };

  const getProgressPercentage = () => {
    if (!stats) return 0;
    return Math.round((stats.processed / stats.totalRows) * 100);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <button
            onClick={() => navigate('/')}
            className="text-blue-600 hover:text-blue-800 mb-4"
          >
            ← Back to Problems
          </button>
          <h1 className="text-3xl font-bold text-center mb-2">CSV Upload</h1>
          <p className="text-gray-600 text-center">
            Upload a CSV file with LeetCode problems to import into the database
          </p>
        </div>

        {/* CSV Format Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-blue-800 mb-2">Required CSV Format:</h3>
          <p className="text-blue-700 text-sm">
            <strong>Columns:</strong> Index, Difficulty, Title, Frequency, Acceptance_Rate, LeetCode_Number, Company_Name
          </p>
          <p className="text-blue-600 text-xs mt-1">
            Note: Index is ignored. Frequency and Acceptance_Rate are company-specific.
          </p>
        </div>

        {/* Upload Section */}
        {!sessionId && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">1. Select CSV File</h2>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <div className="mb-4">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  id="csv-file-input"
                  disabled={uploading}
                />
                <label
                  htmlFor="csv-file-input"
                  className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg cursor-pointer border-0 transition-colors font-medium"
                >
                  Choose File
                </label>
              </div>
              {file ? (
                <p className="text-green-600 mb-4 font-semibold">
                  ✅ Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)
                </p>
              ) : (
                <p className="text-gray-500 mb-4">
                  No CSV file selected
                </p>
              )}
              <button
                onClick={handleUpload}
                disabled={!file || uploading}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {uploading ? 'Uploading...' : 'Upload CSV'}
              </button>
            </div>
          </div>
        )}

        {/* Processing Section */}
        {sessionId && !completed && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">2. Process Problems</h2>
            
            {stats && (
              <div className="mb-6">
                <div className="flex justify-between text-sm text-gray-600 mb-2">
                  <span>Progress: {stats.processed} / {stats.totalRows}</span>
                  <span>{getProgressPercentage()}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                    style={{ width: `${getProgressPercentage()}%` }}
                  ></div>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-center">
                  <div className="bg-green-50 p-3 rounded">
                    <div className="text-2xl font-bold text-green-600">{stats.newProblems}</div>
                    <div className="text-sm text-green-700">New Problems</div>
                  </div>
                  <div className="bg-blue-50 p-3 rounded">
                    <div className="text-2xl font-bold text-blue-600">{stats.updatedProblems}</div>
                    <div className="text-sm text-blue-700">Updated</div>
                  </div>
                  <div className="bg-red-50 p-3 rounded">
                    <div className="text-2xl font-bold text-red-600">{stats.failed}</div>
                    <div className="text-sm text-red-700">Failed</div>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <div className="text-2xl font-bold text-gray-600">{stats.remaining}</div>
                    <div className="text-sm text-gray-700">Remaining</div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 items-center">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={autoProcess}
                  onChange={(e) => setAutoProcess(e.target.checked)}
                  className="mr-2"
                  disabled={processing}
                />
                <span className="text-sm">Auto-process (continue without asking)</span>
              </label>
              
              <button
                onClick={handleProcessBatch}
                disabled={processing || (stats?.remaining || 0) <= 0}
                className="px-6 py-2 rounded-lg font-medium border-0"
                style={{
                  backgroundColor: processing || (stats?.remaining || 0) <= 0 ? '#4B5563' : '#16A34A',
                  color: '#FFFFFF',
                  cursor: processing || (stats?.remaining || 0) <= 0 ? 'not-allowed' : 'pointer'
                }}
              >
                {processing ? 'Processing...' : `Process Next ${Math.min(5, stats?.remaining || 0)} Problems`}
              </button>
            </div>

            {stats && stats.errors.length > 0 && (
              <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-semibold text-red-800 mb-2">Recent Errors:</h4>
                <ul className="text-sm text-red-700">
                  {stats.errors.slice(-3).map((error, idx) => (
                    <li key={idx} className="mb-1">• {error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Completion Summary */}
        {completed && stats && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4 text-green-600">✅ Processing Complete!</h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-green-50 p-4 rounded-lg text-center">
                <div className="text-3xl font-bold text-green-600">{stats.newProblems}</div>
                <div className="text-sm text-green-700">New Problems Created</div>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg text-center">
                <div className="text-3xl font-bold text-blue-600">{stats.updatedProblems}</div>
                <div className="text-sm text-blue-700">Problems Updated</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg text-center">
                <div className="text-3xl font-bold text-gray-600">{stats.processed}</div>
                <div className="text-sm text-gray-700">Total Processed</div>
              </div>
              <div className="bg-red-50 p-4 rounded-lg text-center">
                <div className="text-3xl font-bold text-red-600">{stats.failed}</div>
                <div className="text-sm text-red-700">Failed</div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/')}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
              >
                View All Problems
              </button>
              <button
                onClick={handleReset}
                className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700"
              >
                Upload Another CSV
              </button>
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-red-800 mb-2">Error:</h4>
            <p className="text-red-700">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
};